import React, { useState, useEffect, useCallback } from 'react';
import vendorAPI, {
  Vendor,
  VendorStats,
  AlertSummary,
  VendorFilters
} from '../services/vendorAPI';
import VendorList from '../components/Vendor/VendorList';
import VendorDetail from '../components/Vendor/VendorDetail';
import VendorForm from '../components/Vendor/VendorForm';
import VendorAlerts from '../components/Vendor/VendorAlerts';
import DependencyScanner from '../components/Vendor/DependencyScanner';
import {
  Building2,
  Shield,
  AlertTriangle,
  FileCheck,
  Package,
  Download,
  Plus,
  RefreshCw,
  Search,
  Filter,
  BarChart3,
  Clock,
  FileText
} from 'lucide-react';
import AdminSecurityHelpButton from '../components/Admin/AdminSecurityHelpButton';

type TabType = 'vendors' | 'alerts' | 'dependencies' | 'reports';

const VendorManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('vendors');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [stats, setStats] = useState<VendorStats | null>(null);
  const [alertSummary, setAlertSummary] = useState<AlertSummary | null>(null);

  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);

  const [filters, setFilters] = useState<VendorFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [vendorsData, statsData, alertSummaryData] = await Promise.all([
        vendorAPI.getVendors({ ...filters, search: searchTerm || undefined }),
        vendorAPI.getStats(),
        vendorAPI.getAlertSummary()
      ]);

      setVendors(vendorsData);
      setStats(statsData);
      setAlertSummary(alertSummaryData);
    } catch (error) {
      console.error('Failed to load vendor data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters, searchTerm]);

  const refreshSelectedVendor = useCallback(async () => {
    loadData();
    setSelectedVendor(prev => {
      if (!prev) return prev;
      // Re-fetch full vendor detail so the open modal sees latest reviews immediately
      vendorAPI.getVendorById(prev.id)
        .then(updated => setSelectedVendor(updated))
        .catch(() => {});
      return prev;
    });
  }, [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleVendorClick = async (vendor: Vendor) => {
    try {
      const fullVendor = await vendorAPI.getVendorById(vendor.id);
      setSelectedVendor(fullVendor);
      setIsDetailOpen(true);
    } catch (error) {
      console.error('Failed to load vendor details:', error);
    }
  };

  const handleCreateVendor = () => {
    setEditingVendor(null);
    setIsFormOpen(true);
  };

  const handleEditVendor = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setIsFormOpen(true);
  };

  const handleSaveVendor = async (data: Parameters<typeof vendorAPI.createVendor>[0]) => {
    try {
      if (editingVendor) {
        await vendorAPI.updateVendor(editingVendor.id, data);
      } else {
        await vendorAPI.createVendor(data);
      }
      setIsFormOpen(false);
      setEditingVendor(null);
      loadData();
    } catch (error) {
      console.error('Failed to save vendor:', error);
      throw error;
    }
  };

  const handleArchiveVendor = async (vendor: Vendor, reason: string) => {
    try {
      await vendorAPI.archiveVendor(vendor.id, reason);
      setIsDetailOpen(false);
      setSelectedVendor(null);
      loadData();
    } catch (error) {
      console.error('Failed to archive vendor:', error);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const blob = await vendorAPI.exportPDF();
      vendorAPI.downloadFile(blob, `vendor-audit-report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const blob = await vendorAPI.exportExcel();
      vendorAPI.downloadFile(blob, `vendor-report-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Failed to export Excel:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportComplianceMatrix = async () => {
    setIsExporting(true);
    try {
      const blob = await vendorAPI.exportComplianceMatrix();
      vendorAPI.downloadFile(blob, `compliance-matrix-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Failed to export compliance matrix:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportSubcontractorList = async () => {
    setIsExporting(true);
    try {
      const blob = await vendorAPI.exportSubcontractorList();
      vendorAPI.downloadFile(blob, `subcontractor-list-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Failed to export subcontractor list:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const toggleSubcontractorsOnly = () => {
    setFilters(prev => ({
      ...prev,
      subcontractors_only: !prev.subcontractors_only
    }));
  };

  const tier1Vendors = vendors.filter(v => v.tier === 1);
  const subcontractors = vendors.filter(v => v.subcontractor_type && v.subcontractor_type !== 'not_subcontractor');
  const tier2Vendors = vendors.filter(v => v.tier === 2);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Vendor Management</h1>
          <p className="text-gray-600">
            Track vendor security, compliance, and risk for SOC 2 and client audits
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AdminSecurityHelpButton screenId="vendor-management" />
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <FileText className="w-4 h-4" />
            Export PDF
          </button>
          <button
            onClick={handleExportExcel}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
          <button
            onClick={handleCreateVendor}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Vendor
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Vendors</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-blue-600 mt-1">
                  {stats.tier1Count} Critical (Tier 1)
                </p>
              </div>
              <Building2 className="h-10 w-10 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">SOC 2 Status</p>
                <p className="text-2xl font-bold text-green-600">{stats.soc2Current}</p>
                <p className="text-xs text-red-600 mt-1">
                  {stats.soc2Expired} expired
                </p>
              </div>
              <Shield className="h-10 w-10 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">DPA Status</p>
                <p className="text-2xl font-bold text-green-600">{stats.dpaSigned}</p>
                <p className="text-xs text-orange-600 mt-1">
                  {stats.dpaPending} pending
                </p>
              </div>
              <FileCheck className="h-10 w-10 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Reviews</p>
                <p className="text-2xl font-bold text-green-600">{stats.reviewsCurrent}</p>
                <p className="text-xs text-red-600 mt-1">
                  {stats.reviewsOverdue} overdue
                </p>
              </div>
              <Clock className="h-10 w-10 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Active Alerts</p>
                <p className="text-2xl font-bold text-orange-600">{stats.activeAlerts}</p>
                <p className="text-xs text-red-600 mt-1">
                  {stats.criticalAlerts} critical
                </p>
              </div>
              <AlertTriangle className="h-10 w-10 text-orange-600 opacity-20" />
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex gap-8">
            {[
              { id: 'vendors' as TabType, label: 'Vendors', icon: Building2 },
              { id: 'alerts' as TabType, label: 'Alerts', icon: AlertTriangle, badge: alertSummary?.total },
              { id: 'dependencies' as TabType, label: 'Dependencies', icon: Package },
              { id: 'reports' as TabType, label: 'Reports', icon: BarChart3 }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {activeTab === 'vendors' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <button
                onClick={loadData}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4">
                <select
                  value={filters.tier || ''}
                  onChange={(e) => setFilters({ ...filters, tier: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All Tiers</option>
                  <option value="1">Tier 1 (Critical)</option>
                  <option value="2">Tier 2 (Standard)</option>
                </select>
                <select
                  value={filters.soc2_status || ''}
                  onChange={(e) => setFilters({ ...filters, soc2_status: e.target.value || undefined })}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All SOC 2 Status</option>
                  <option value="current">Current</option>
                  <option value="expired">Expired</option>
                  <option value="pending">Pending</option>
                  <option value="not_applicable">N/A</option>
                </select>
                <select
                  value={filters.dpa_status || ''}
                  onChange={(e) => setFilters({ ...filters, dpa_status: e.target.value || undefined })}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All DPA Status</option>
                  <option value="signed">Signed</option>
                  <option value="pending">Pending</option>
                  <option value="not_required">Not Required</option>
                </select>
                <select
                  value={filters.risk_level || ''}
                  onChange={(e) => setFilters({ ...filters, risk_level: e.target.value || undefined })}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All Risk Levels</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <button
                  onClick={toggleSubcontractorsOnly}
                  className={`px-3 py-2 border rounded-lg transition-colors flex items-center gap-2 ${
                    filters.subcontractors_only
                      ? 'bg-orange-50 border-orange-300 text-orange-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Building2 className="w-4 h-4" />
                  {filters.subcontractors_only ? 'Showing Subcontractors' : 'Show Subcontractors Only'}
                </button>
              </div>
            )}
          </div>

          {tier1Vendors.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-600" />
                Tier 1 - Critical Vendors ({tier1Vendors.length})
              </h2>
              <VendorList
                vendors={tier1Vendors}
                onVendorClick={handleVendorClick}
                onEdit={handleEditVendor}
                isLoading={isLoading}
              />
            </div>
          )}

          {tier2Vendors.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                Tier 2 - Standard Vendors ({tier2Vendors.length})
              </h2>
              <VendorList
                vendors={tier2Vendors}
                onVendorClick={handleVendorClick}
                onEdit={handleEditVendor}
                isLoading={isLoading}
              />
            </div>
          )}

          {vendors.length === 0 && !isLoading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No vendors found</h3>
              <p className="text-gray-500 mb-4">Add your first vendor to start tracking compliance.</p>
              <button
                onClick={handleCreateVendor}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                Add Vendor
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'alerts' && (
        <VendorAlerts
          onViewVendor={(vendorId) => {
            const vendor = vendors.find(v => v.id === vendorId);
            if (vendor) handleVendorClick(vendor);
          }}
        />
      )}

      {activeTab === 'dependencies' && (
        <DependencyScanner />
      )}

      {activeTab === 'reports' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Audit Reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Vendor Audit Report</h3>
                  <p className="text-sm text-gray-500">PDF format</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Complete vendor inventory with SOC 2 status, DPA compliance, risk scores, and review history.
              </p>
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg p-6 hover:border-green-300 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Vendor Data Export</h3>
                  <p className="text-sm text-gray-500">Excel format</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Detailed spreadsheet with all vendor data, dependencies, and alerts for analysis.
              </p>
              <button
                onClick={handleExportExcel}
                disabled={isExporting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Download Excel
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg p-6 hover:border-purple-300 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Compliance Matrix</h3>
                  <p className="text-sm text-gray-500">SOC 2 Ready</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                CC9.1/CC9.2 evidence package showing vendor risk assessment and monitoring.
              </p>
              <button
                onClick={handleExportComplianceMatrix}
                disabled={isExporting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Generate Evidence
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg p-6 hover:border-orange-300 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Subcontractor List</h3>
                  <p className="text-sm text-gray-500">Q21 Evidence</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Third-party vendors with customer data access for questionnaire Q21 compliance.
              </p>
              <button
                onClick={handleExportSubcontractorList}
                disabled={isExporting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Download List
              </button>
            </div>
          </div>
        </div>
      )}

      {isDetailOpen && selectedVendor && (
        <VendorDetail
          vendor={selectedVendor}
          onClose={() => {
            setIsDetailOpen(false);
            setSelectedVendor(null);
          }}
          onEdit={() => handleEditVendor(selectedVendor)}
          onArchive={(reason) => handleArchiveVendor(selectedVendor, reason)}
          onUpdate={refreshSelectedVendor}
        />
      )}

      {isFormOpen && (
        <VendorForm
          vendor={editingVendor}
          onClose={() => {
            setIsFormOpen(false);
            setEditingVendor(null);
          }}
          onSave={handleSaveVendor}
        />
      )}
    </div>
  );
};

export default VendorManagement;
